import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFundingType } from "@/hooks/useFundingType";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useSector } from "@/hooks/useSector";
import { getCityByCountry } from "@/services/api/Master";
import {
  getUnicornRegionCountries,
  getUnicornRegions,
} from "@/services/api/UnicornTracker";
import { CaretDown, Check } from "@phosphor-icons/react/dist/ssr";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function UnicornFilters({ open, onOpenChange }) {
  const isMobile = useIsMobile();

  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegionId, setSelectedRegionId] = useState("");
  const [searchSectorQuery, setSearchSectorQuery] = useState("");
  const [searchCountryQuery, setSearchCountryQuery] = useState("");
  const [searchCityQuery, setSearchCityQuery] = useState("");
  const [searchFundingStageQuery, setSearchFundingStageQuery] = useState("");
  const [openCountrySelector, setOpenCountrySelector] = useState(false);
  const [openCitySelector, setOpenCitySelector] = useState(false);
  const [openSectorSelector, setOpenSectorSelector] = useState(false);
  const [openFundingStageSelector, setOpenFundingStageSelector] =
    useState(false);
  const [localFilters, setLocalFilters] = useState({
    regions: [],
    countries: [],
    sectors: [],
    cities: [],
    fundingStages: [],
    valuationCondition: "",
    valuationAmount: "",
    yearFoundedCondition: "",
    yearFoundedYear: "",
    timeToUnicornMin: "",
    timeToUnicornMax: "",
    status: ["current", "fallen", "alumni"],
    originFilter: "",
  });

  const router = useRouter();
  const searchParams = useSearchParams();

  const { data: sectors, isLoading: sectorsLoading } = useSector({
    dropdown: true,
  });

  const { data: fundingStages, isLoading: fundingStagesLoading } =
    useFundingType({
      searchStartupDropdown: true,
    });

  const filteredSectors = sectors
    .filter((sector) =>
      sector.name.toLowerCase().includes(searchSectorQuery.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredCountries = countries.filter((country) =>
    (country.name || country.country_name)
      .toLowerCase()
      .includes(searchCountryQuery.toLowerCase()),
  );

  const filteredCities = cities
    .filter((city) =>
      city.name.toLowerCase().includes(searchCityQuery.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredFundingStages = fundingStages.filter((stage) =>
    stage.name.toLowerCase().includes(searchFundingStageQuery.toLowerCase()),
  );

  const getSelectedSectorsText = () => {
    if (localFilters.sectors.length === 0) return "All Sectors";
    if (localFilters.sectors.length === 1) {
      const s = sectors.find(
        (sec) => sec.id.toString() === localFilters.sectors[0],
      );
      return s ? s.name : "Unknown Sector";
    }
    return `${localFilters.sectors.length} Sectors Selected`;
  };

  const getSelectedFundingStagesText = () => {
    if (localFilters.fundingStages.length === 0) return "All Stages";
    if (localFilters.fundingStages.length === 1) {
      const s = fundingStages.find(
        (stage) => stage.id.toString() === localFilters.fundingStages[0],
      );
      return s ? s.name : "Unknown Stage";
    }
    return `${localFilters.fundingStages.length} Stages Selected`;
  };

  const getCountryId = (country) => {
    const cid = country?.country_id ?? country?.id;
    return cid?.toString();
  };

  const getCountryName = (countryId) => {
    if (!countryId) return "Select Country";
    const country = countries.find((c) => {
      const cid = c.country_id ?? c.id;
      return cid && cid.toString() === countryId.toString();
    });
    return country ? country.country_name || country.name : "Select Country";
  };

  const currentCountryName = useMemo(() => {
    return getCountryName(localFilters.countries[0]);
  }, [localFilters.countries, countries]);

  const getCityName = (cityId) => {
    if (!cityId) return "Select City";
    const city = cities.find((c) => c.id?.toString() === cityId?.toString());
    return city ? city.name : "Select City";
  };

  const currentCityName = useMemo(() => {
    return getCityName(localFilters.cities[0]);
  }, [localFilters.cities, cities]);

  // Initialize filters from URL parameters
  useEffect(() => {
    const urlRegionId = searchParams.get("unicorn_regions");
    const urlCountryId = searchParams.get("country");
    const urlSectors = searchParams.get("sectors");
    const urlCities = searchParams.get("cities");
    const urlFundingStages = searchParams.get("stages");
    const urlValuationCondition = searchParams.get("valuation_condition");
    const urlValuationAmount = searchParams.get("valuation_amount");
    const urlYearFoundedCondition = searchParams.get("year_founded_condition");
    const urlYearFoundedYear = searchParams.get("year_founded_year");
    const urlTimeToUnicornMin = searchParams.get("min_time_to_unicorn");
    const urlTimeToUnicornMax = searchParams.get("max_time_to_unicorn");
    const urlStatus = searchParams.get("unicorn_status");
    const urlOriginFilter = searchParams.get("origin_filter");

    if (urlRegionId) {
      setSelectedRegionId(urlRegionId);
    }

    const newFilters = {
      regions: urlRegionId ? [urlRegionId] : [],
      countries: urlCountryId ? [urlCountryId.toString()] : [],
      sectors: urlSectors ? urlSectors.split(",") : [],
      cities: urlCities ? urlCities.split(",") : [],
      fundingStages: urlFundingStages ? urlFundingStages.split(",") : [],
      valuationCondition: urlValuationCondition || "",
      valuationAmount: urlValuationAmount || "",
      yearFoundedCondition: urlYearFoundedCondition || "",
      yearFoundedYear: urlYearFoundedYear || "",
      timeToUnicornMin: urlTimeToUnicornMin || "",
      timeToUnicornMax: urlTimeToUnicornMax || "",
      status: urlStatus
        ? urlStatus.split(",")
        : ["current", "fallen", "alumni"],
      originFilter: urlOriginFilter || "",
    };

    setLocalFilters(newFilters);
  }, [searchParams]);

  useEffect(() => {
    async function fetchFilterData() {
      try {
        const [regionsRes] = await Promise.all([
          getUnicornRegions({ params: { dropdown: true, has_unicorn: true } }),
        ]);

        setRegions(regionsRes.data || []);
        setCountries([]);
        setCities([]);
      } catch (error) {
        console.error("Error fetching filter data:", error);
      } finally {
        setLoading(false);
      }
    }

    if (open) {
      fetchFilterData();
    }
  }, [open]);

  // Fetch countries when region is selected
  useEffect(() => {
    async function fetchRegionCountries() {
      if (selectedRegionId && regions.length > 0) {
        try {
          const regionCountriesRes = await getUnicornRegionCountries(
            selectedRegionId,
            {
              params: { dropdown: true, searchStartupDropdown: true },
            },
          );
          setCountries(regionCountriesRes.data || []);
          setCities([]);
          const urlCountryId = searchParams.get("country");
          setLocalFilters((prev) => ({
            ...prev,
            cities: [],
            countries: urlCountryId ? [urlCountryId.toString()] : [],
          }));
        } catch (error) {
          console.error("Error fetching region countries:", error);
          setCountries([]);
        }
      } else {
        setCountries([]);
        setCities([]);
        setLocalFilters((prev) => ({ ...prev, countries: [], cities: [] }));
      }
    }

    fetchRegionCountries();
  }, [selectedRegionId, regions, searchParams]);

  // Fetch cities when country is selected
  useEffect(() => {
    async function fetchCountryCities() {
      if (localFilters.countries.length > 0) {
        try {
          const countryId = localFilters.countries[0];
          const citiesRes = await getCityByCountry(countryId);
          setCities(citiesRes.data || []);
        } catch (error) {
          console.error("Error fetching cities for country:", error);
          setCities([]);
        }
      } else {
        setCities([]);
      }
    }

    fetchCountryCities();
  }, [localFilters.countries]);

  const handleRegionSelect = (regionId) => {
    setSelectedRegionId(regionId);
    setLocalFilters((prev) => ({
      ...prev,
      regions: regionId ? [regionId] : [],
      countries: [],
    }));
  };

  const handleCountryToggle = (countryId) => {
    const countryIdStr = countryId.toString();
    setLocalFilters((prev) => ({
      ...prev,
      countries: prev.countries.includes(countryIdStr) ? [] : [countryIdStr],
      cities: prev.countries.includes(countryIdStr) ? [] : prev.cities,
    }));
  };

  const handleCityToggle = (cityId) => {
    setLocalFilters((prev) => ({
      ...prev,
      cities: prev.cities.includes(cityId)
        ? prev.cities.filter((c) => c !== cityId)
        : [...prev.cities, cityId],
    }));
  };

  const handleValuationChange = (condition, amount) => {
    setLocalFilters((prev) => ({
      ...prev,
      valuationCondition: condition,
      valuationAmount: amount,
    }));
  };

  const handleYearFoundedChange = (condition, year) => {
    setLocalFilters((prev) => ({
      ...prev,
      yearFoundedCondition: condition,
      yearFoundedYear: year,
    }));
  };

  const handleTimeToUnicornChange = (min, max) => {
    setLocalFilters((prev) => ({
      ...prev,
      timeToUnicornMin: min,
      timeToUnicornMax: max,
    }));
  };

  const handleClearAll = () => {
    setSelectedRegionId("");
    setLocalFilters({
      regions: [],
      countries: [],
      sectors: [],
      cities: [],
      fundingStages: [],
      valuationCondition: "",
      valuationAmount: "",
      yearFoundedCondition: "",
      yearFoundedYear: "",
      timeToUnicornMin: "",
      timeToUnicornMax: "",
      status: ["current", "fallen", "alumni"],
      originFilter: "",
    });

    router.push("?unicorn_regions=1", { scroll: false });
    onOpenChange(false);
  };

  const hasActiveGeographicalFilters = () => {
    return (
      selectedRegionId ||
      localFilters.countries.length > 0 ||
      localFilters.cities.length > 0
    );
  };

  const hasActiveSectorFilters = () => {
    return localFilters.sectors.length > 0;
  };

  const hasActiveFinancialFilters = () => {
    return (
      localFilters.fundingStages.length > 0 ||
      (localFilters.valuationCondition && localFilters.valuationAmount)
    );
  };

  const hasActiveBasicInfoFilters = () => {
    return (
      (localFilters.yearFoundedCondition && localFilters.yearFoundedYear) ||
      localFilters.timeToUnicornMin ||
      localFilters.timeToUnicornMax
    );
  };

  const hasActiveStatusFilters = () => {
    return localFilters.status.length !== 3;
  };

  const hasActiveOriginFilters = () => {
    return !!localFilters.originFilter;
  };

  const handleOriginFilterChange = (value) => {
    setLocalFilters((prev) => ({
      ...prev,
      originFilter: prev.originFilter === value ? "" : value,
    }));
  };

  const handleStatusToggle = (key) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: prev.status.includes(key)
        ? prev.status.filter((s) => s !== key)
        : [...prev.status, key],
    }));
  };

  const handleShowUnicorns = () => {
    const params = new URLSearchParams(searchParams);

    if (selectedRegionId) {
      params.set("unicorn_regions", selectedRegionId);
    } else {
      params.delete("unicorn_regions");
    }

    if (localFilters.countries.length > 0) {
      params.set("country", localFilters.countries[0]);
    } else {
      params.delete("country");
    }

    if (localFilters.sectors.length > 0) {
      params.set("sectors", localFilters.sectors.join(","));
    } else {
      params.delete("sectors");
    }

    if (localFilters.cities.length > 0) {
      params.set("cities", localFilters.cities.join(","));
    } else {
      params.delete("cities");
    }

    if (localFilters.fundingStages.length > 0) {
      params.set("stages", localFilters.fundingStages.join(","));
    } else {
      params.delete("stages");
    }

    if (localFilters.valuationCondition && localFilters.valuationAmount) {
      params.set("valuation_condition", localFilters.valuationCondition);
      params.set("valuation_amount", localFilters.valuationAmount);
    } else {
      params.delete("valuation_condition");
      params.delete("valuation_amount");
    }

    if (localFilters.yearFoundedCondition && localFilters.yearFoundedYear) {
      params.set("year_founded_condition", localFilters.yearFoundedCondition);
      params.set("year_founded_year", localFilters.yearFoundedYear);
    } else {
      params.delete("year_founded_condition");
      params.delete("year_founded_year");
    }

    if (localFilters.timeToUnicornMin || localFilters.timeToUnicornMax) {
      if (localFilters.timeToUnicornMin) {
        params.set("min_time_to_unicorn", localFilters.timeToUnicornMin);
      } else {
        params.delete("min_time_to_unicorn");
      }
      if (localFilters.timeToUnicornMax) {
        params.set("max_time_to_unicorn", localFilters.timeToUnicornMax);
      } else {
        params.delete("max_time_to_unicorn");
      }
    } else {
      params.delete("min_time_to_unicorn");
      params.delete("max_time_to_unicorn");
    }

    if (localFilters.status.length === 0) {
      params.set("unicorn_status", "current,fallen,alumni");
    } else {
      params.set("unicorn_status", localFilters.status.join(","));
    }

    if (localFilters.originFilter) {
      params.set("origin_filter", localFilters.originFilter);
    } else {
      params.delete("origin_filter");
    }

    router.push(`?${params.toString()}`, { scroll: false });
    onOpenChange(false);
  };

  // ─── Shared filter content sections ───────────────────────────────

  const GeographicalContent = (
    <div className='space-y-6'>
      <div>
        <p className='text-sm'>Region</p>
        <div className='mt-2.5'>
          {loading ? (
            <div className='text-xs opacity-50'>Loading regions...</div>
          ) : (
            <Select value={selectedRegionId} onValueChange={handleRegionSelect}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select a region' />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id.toString()}>
                    {region.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      {selectedRegionId && (
        <div>
          <p className='text-sm'>Countries</p>
          <div className='mt-2.5'>
            {loading ? (
              <div className='text-xs opacity-50'>Loading countries...</div>
            ) : countries.length > 0 ? (
              <div className='relative'>
                <button
                  onClick={() => setOpenCountrySelector((prev) => !prev)}
                  className='inline-flex items-center gap-3 px-4 py-2 pr-3 text-xs rounded-md bg-white/10'
                >
                  <span className='truncate max-w-48'>
                    {currentCountryName}
                  </span>
                  <CaretDown weight='bold' className='text-base' />
                </button>
                {openCountrySelector && (
                  <div className='w-full p-0 mt-2 border rounded-md border-white/5 bg-black/95 backdrop-blur-md'>
                    <div className='p-2 border-b border-white/5'>
                      <Input
                        type='text'
                        placeholder='Search countries...'
                        value={searchCountryQuery}
                        onChange={(e) => setSearchCountryQuery(e.target.value)}
                        className='h-8 text-xs'
                      />
                    </div>
                    <ScrollArea className='h-64'>
                      <div className='p-1'>
                        <div
                          onClick={() => {
                            setLocalFilters((prev) => ({
                              ...prev,
                              countries: [],
                              cities: [],
                            }));
                            setOpenCountrySelector(false);
                            setSearchCountryQuery("");
                          }}
                          className='px-3 py-2 text-xs text-orange-400 cursor-pointer hover:bg-white/10'
                        >
                          All Countries
                        </div>
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <div
                              key={country.country_id ?? country.id}
                              onClick={() => {
                                const id = getCountryId(country);
                                setLocalFilters((prev) => ({
                                  ...prev,
                                  countries: id ? [id] : [],
                                  cities: [],
                                }));
                                setOpenCountrySelector(false);
                                setSearchCountryQuery("");
                              }}
                              className='px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                            >
                              {country.country_name ||
                                country.name ||
                                "Unknown Country"}
                            </div>
                          ))
                        ) : (
                          <div className='px-3 py-2 text-xs opacity-50'>
                            {searchCountryQuery
                              ? "No countries found"
                              : "No countries available"}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ) : (
              <div className='text-xs opacity-50'>
                No countries available for this region
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const SectorsContent = (
    <div className='space-y-6'>
      <div>
        <p className='text-sm'>Sectors</p>
        <div className='mt-2.5'>
          {loading || sectorsLoading ? (
            <div className='text-xs opacity-50'>Loading sectors...</div>
          ) : (
            <div className='relative'>
              <button
                onClick={() => setOpenSectorSelector((prev) => !prev)}
                className='inline-flex items-center justify-between w-full px-4 py-2 text-xs rounded-md bg-white/10'
              >
                <span className='truncate'>{getSelectedSectorsText()}</span>
                <CaretDown weight='bold' className='text-base' />
              </button>
              {openSectorSelector && (
                <div className='w-full p-0 mt-2 border rounded-md border-white/5 bg-black/95 backdrop-blur-md'>
                  <div className='p-2 border-b border-white/5'>
                    <Input
                      type='text'
                      placeholder='Search sectors...'
                      value={searchSectorQuery}
                      onChange={(e) => setSearchSectorQuery(e.target.value)}
                      className='h-8 text-xs'
                    />
                  </div>
                  <ScrollArea className='h-64'>
                    <div className='p-1'>
                      <div
                        onClick={() => {
                          setLocalFilters((prev) => ({
                            ...prev,
                            sectors: [],
                          }));
                        }}
                        className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                      >
                        <span
                          className={
                            localFilters.sectors.length === 0
                              ? "text-orange-400"
                              : ""
                          }
                        >
                          Select All
                        </span>
                        {localFilters.sectors.length === 0 && (
                          <Check weight='bold' className='text-orange-400' />
                        )}
                      </div>
                      {filteredSectors
                        .filter((s) => s.name)
                        .map((sector) => {
                          const isSelected = localFilters.sectors.includes(
                            sector.id.toString(),
                          );
                          return (
                            <div
                              key={sector.id}
                              onClick={() => {
                                setLocalFilters((prev) => ({
                                  ...prev,
                                  sectors: prev.sectors.includes(
                                    sector.id.toString(),
                                  )
                                    ? prev.sectors.filter(
                                        (s) => s !== sector.id.toString(),
                                      )
                                    : [...prev.sectors, sector.id.toString()],
                                }));
                              }}
                              className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                            >
                              <span
                                className={isSelected ? "text-orange-400" : ""}
                              >
                                {sector.name}
                              </span>
                              {isSelected && (
                                <Check
                                  weight='bold'
                                  className='text-orange-400'
                                />
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const FinancialsContent = (
    <div className='space-y-6'>
      <div>
        <p className='text-sm'>Funding Stage</p>
        <div className='mt-2.5'>
          {fundingStagesLoading ? (
            <div className='text-xs opacity-50'>Loading funding stages...</div>
          ) : (
            <div className='relative'>
              <button
                onClick={() => setOpenFundingStageSelector((prev) => !prev)}
                className='inline-flex items-center justify-between w-full px-4 py-2 text-xs rounded-md bg-white/10'
              >
                <span className='truncate'>
                  {getSelectedFundingStagesText()}
                </span>
                <CaretDown weight='bold' className='text-base' />
              </button>
              {openFundingStageSelector && (
                <div className='w-full p-0 mt-2 border rounded-md border-white/5 bg-black/95 backdrop-blur-md'>
                  <div className='p-2 border-b border-white/5'>
                    <Input
                      type='text'
                      placeholder='Search funding stages...'
                      value={searchFundingStageQuery}
                      onChange={(e) =>
                        setSearchFundingStageQuery(e.target.value)
                      }
                      className='h-8 text-xs'
                    />
                  </div>
                  <ScrollArea className='h-64'>
                    <div className='p-1'>
                      <div
                        onClick={() => {
                          setLocalFilters((prev) => ({
                            ...prev,
                            fundingStages: [],
                          }));
                        }}
                        className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                      >
                        <span
                          className={
                            localFilters.fundingStages.length === 0
                              ? "text-orange-400"
                              : ""
                          }
                        >
                          Select All
                        </span>
                        {localFilters.fundingStages.length === 0 && (
                          <Check weight='bold' className='text-orange-400' />
                        )}
                      </div>
                      {filteredFundingStages.map((stage) => {
                        const isSelected = localFilters.fundingStages.includes(
                          stage.id.toString(),
                        );
                        return (
                          <div
                            key={stage.id}
                            onClick={() => {
                              setLocalFilters((prev) => ({
                                ...prev,
                                fundingStages: prev.fundingStages.includes(
                                  stage.id.toString(),
                                )
                                  ? prev.fundingStages.filter(
                                      (s) => s !== stage.id.toString(),
                                    )
                                  : [
                                      ...prev.fundingStages,
                                      stage.id.toString(),
                                    ],
                              }));
                            }}
                            className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                          >
                            <span
                              className={isSelected ? "text-orange-400" : ""}
                            >
                              {stage.name}
                            </span>
                            {isSelected && (
                              <Check
                                weight='bold'
                                className='text-orange-400'
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <p className='text-sm'>Startup Valuation</p>
        <div className='flex mt-2.5 gap-4'>
          <Select
            value={localFilters.valuationCondition}
            onValueChange={(value) =>
              handleValuationChange(value, localFilters.valuationAmount)
            }
          >
            <SelectTrigger className='flex-1'>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='less'>is less than</SelectItem>
              <SelectItem value='more'>is more than</SelectItem>
            </SelectContent>
          </Select>
          <div className='relative flex-1'>
            <Input
              placeholder='100'
              className='pr-12'
              value={localFilters.valuationAmount}
              onChange={(e) =>
                handleValuationChange(
                  localFilters.valuationCondition,
                  e.target.value,
                )
              }
            />
            <span className='absolute text-xs -translate-y-1/2 top-1/2 right-3 opacity-40'>
              Billions USD
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const BasicInfoContent = (
    <div className='space-y-6'>
      <div>
        <p className='text-sm'>Year Founded</p>
        <div className='flex mt-2.5 gap-4'>
          <Select
            value={localFilters.yearFoundedCondition}
            onValueChange={(value) =>
              handleYearFoundedChange(value, localFilters.yearFoundedYear)
            }
          >
            <SelectTrigger className='flex-1'>
              <SelectValue placeholder='Select' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='less'>is less than</SelectItem>
              <SelectItem value='more'>is more than</SelectItem>
            </SelectContent>
          </Select>
          <div className='relative flex-1'>
            <Input
              placeholder={
                localFilters.yearFoundedCondition === "less"
                  ? "ie. 2025"
                  : localFilters.yearFoundedCondition === "more"
                    ? "ie. 2000"
                    : "ie. 2025"
              }
              className='pr-12'
              value={localFilters.yearFoundedYear}
              onChange={(e) =>
                handleYearFoundedChange(
                  localFilters.yearFoundedCondition,
                  e.target.value,
                )
              }
            />
          </div>
        </div>
      </div>
      <div>
        <p className='text-sm'>Time to Unicorn</p>
        <div className='flex mt-2.5 gap-4'>
          <div className='relative flex-1'>
            <span className='absolute text-xs -translate-y-1/2 top-1/2 left-3 opacity-40'>
              Min
            </span>
            <Input
              placeholder='ie. 0'
              className='pl-10 pr-12'
              value={localFilters.timeToUnicornMin}
              onChange={(e) =>
                handleTimeToUnicornChange(
                  e.target.value,
                  localFilters.timeToUnicornMax,
                )
              }
            />
            <span className='absolute text-xs -translate-y-1/2 top-1/2 right-3 opacity-40'>
              Years
            </span>
          </div>
          <div className='relative flex-1'>
            <span className='absolute text-xs -translate-y-1/2 top-1/2 left-3 opacity-40'>
              Max
            </span>
            <Input
              placeholder='ie. 10'
              className='pl-10 pr-12'
              value={localFilters.timeToUnicornMax}
              onChange={(e) =>
                handleTimeToUnicornChange(
                  localFilters.timeToUnicornMin,
                  e.target.value,
                )
              }
            />
            <span className='absolute text-xs -translate-y-1/2 top-1/2 right-3 opacity-40'>
              Years
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Determine if we have a country context to show directional options
  const hasCountryContext = localFilters.countries.length > 0 || searchParams.get("country");

  const originOptions = hasCountryContext
    ? [
        { key: "homegrown", label: "Homegrown", description: "Founded and HQ'd in the same country" },
        { key: "transplant_in", label: "Transplant-In", description: "Relocated INTO this country from elsewhere" },
        { key: "transplant_out", label: "Transplant-Out", description: "Founded here but HQ'd elsewhere" },
      ]
    : [
        { key: "homegrown", label: "Homegrown", description: "Founded and HQ'd in the same country" },
        { key: "transplant", label: "Transplant", description: "Founded in a different country than current HQ" },
      ];

  const OriginContent = (
    <div className='space-y-3'>
      <p className='text-sm'>Established In</p>
      {originOptions.map(({ key, label, description }) => {
        const isSelected = localFilters.originFilter === key;
        return (
          <div
            key={key}
            onClick={() => handleOriginFilterChange(key)}
            className='flex items-center justify-between px-3 py-2.5 text-xs rounded-md cursor-pointer hover:bg-white/10 border border-white/5'
          >
            <div>
              <p className={isSelected ? "text-purple-400 font-medium" : ""}>
                {label}
              </p>
              <p className='mt-0.5 opacity-40'>{description}</p>
            </div>
            {isSelected && (
              <Check weight='bold' className='text-purple-400 shrink-0' />
            )}
          </div>
        );
      })}
    </div>
  );

  const StatusContent = (
    <div className='space-y-3'>
      <p className='text-sm'>Status</p>
      {[
        {
          key: "current",
          label: "Current",
          description: "Active unicorns valued at US $1B+",
        },
        {
          key: "fallen",
          label: "Fallen",
          description: "Previously US $1B+, now below",
        },
        {
          key: "alumni",
          label: "Alumni",
          description: "Gone public (IPO) or acquired",
        },
      ].map(({ key, label, description }) => {
        const isSelected = localFilters.status.includes(key);
        return (
          <div
            key={key}
            onClick={() => handleStatusToggle(key)}
            className='flex items-center justify-between px-3 py-2.5 text-xs rounded-md cursor-pointer hover:bg-white/10 border border-white/5'
          >
            <div>
              <p className={isSelected ? "text-orange-400 font-medium" : ""}>
                {label}
              </p>
              <p className='mt-0.5 opacity-40'>{description}</p>
            </div>
            {isSelected && (
              <Check weight='bold' className='text-orange-400 shrink-0' />
            )}
          </div>
        );
      })}
    </div>
  );

  const FooterActions = (
    <div className='flex items-center justify-between px-5 py-3 border-t'>
      <button
        onClick={handleClearAll}
        className='text-xs transition-colors hover:text-white/80'
      >
        Clear All
      </button>
      <Button onClick={handleShowUnicorns} className='h-auto px-4 py-2 text-xs'>
        Show Unicorns
      </Button>
    </div>
  );

  // ─── Active filter indicator dot ──────────────────────────────────

  const ActiveDot = ({ active }) =>
    active ? (
      <span className='mr-2 bg-red-600 rounded-full size-2 shrink-0'></span>
    ) : null;

  // ─── Mobile: Sheet + Accordion ────────────────────────────────────

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side='bottom' className='h-[85vh] p-0 flex flex-col'>
          <SheetHeader className='py-4 border-b border-white/10'>
            <SheetTitle className='text-sm text-center'>
              Advanced Filter
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className='flex-1'>
            <Accordion type='multiple' className='px-4'>
              <AccordionItem value='geographical'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveGeographicalFilters()} />
                    Geographical
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {GeographicalContent}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value='sectors'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveSectorFilters()} />
                    Sectors
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {SectorsContent}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value='funding'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveFinancialFilters()} />
                    Financials
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {FinancialsContent}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value='basic_info'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveBasicInfoFilters()} />
                    Basic Information
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {BasicInfoContent}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value='origin'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveOriginFilters()} />
                    Established In
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {OriginContent}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value='status'>
                <AccordionTrigger className='text-sm'>
                  <span className='flex items-center'>
                    <ActiveDot active={hasActiveStatusFilters()} />
                    Status
                  </span>
                </AccordionTrigger>
                <AccordionContent className='pb-4'>
                  {StatusContent}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </ScrollArea>
          {FooterActions}
        </SheetContent>
      </Sheet>
    );
  }

  // ─── Desktop: Dialog + Tabs (original layout) ────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[700px] p-0 gap-0 border-0'>
        <div className='flex-1'>
          <div className='py-4 text-sm text-center border-b border-white/10'>
            Advanced Filter
          </div>
          <Tabs defaultValue='geographical' className='flex items-start'>
            <TabsList className='flex flex-col w-40 h-auto gap-4 p-0 py-4 bg-transparent rounded-none'>
              <TabsTrigger
                value='geographical'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveGeographicalFilters()} />
                Geographical
              </TabsTrigger>
              <TabsTrigger
                value='sectors'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveSectorFilters()} />
                Sectors
              </TabsTrigger>
              <TabsTrigger
                value='funding'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveFinancialFilters()} />
                Financials
              </TabsTrigger>
              <TabsTrigger
                value='basic_info'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveBasicInfoFilters()} />
                Basic Information
              </TabsTrigger>
              <TabsTrigger
                value='origin'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveOriginFilters()} />
                Established In
              </TabsTrigger>
              <TabsTrigger
                value='status'
                className='relative justify-end w-full p-0 text-right'
              >
                <ActiveDot active={hasActiveStatusFilters()} />
                Status
              </TabsTrigger>
            </TabsList>
            <div className='flex-1 px-5 ml-5 border-l border-white/10'>
              <TabsContent
                value='geographical'
                className='py-4 mt-0 min-h-[200px]'
              >
                {GeographicalContent}
              </TabsContent>
              <TabsContent value='sectors' className='py-4 mt-0 min-h-[200px]'>
                {SectorsContent}
              </TabsContent>
              <TabsContent value='funding' className='py-4 mt-0 min-h-[200px]'>
                {FinancialsContent}
              </TabsContent>
              <TabsContent
                value='basic_info'
                className='py-4 mt-0 min-h-[200px]'
              >
                {BasicInfoContent}
              </TabsContent>
              <TabsContent value='origin' className='py-4 mt-0 min-h-[200px]'>
                {OriginContent}
              </TabsContent>
              <TabsContent value='status' className='py-4 mt-0 min-h-[200px]'>
                {StatusContent}
              </TabsContent>
              {FooterActions}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
